'use strict';
import './mx-calendar-resource/mx-calendar-resource.component';

import CalendarController from './mx-calendar.controller';
import template from './mx-calendar.html';

/**
 * @ngdoc component
 * @name mx.components:mxCalendar
 * @module mx.components
 *
 * @description
 * Visualizes data/event-items on calendar.
 * mxCalendar expects `items` attribute as an array of objects with keys like:
 * `title`, `start`, `end`, `allDay`.
 * Full list of event options is the same as for `fullCalendar`
 * and can be found here: http://fullcalendar.io/docs/event_data/Event_Object/
 *
 *
 * @usage
 * vm.items = [
 *        {title: 'Today event', start: new Date()},
 *        {title: 'First 2000 day', start: '2000-01-01'},
 *        ...
 * ]
 * ```html
 * <mx-calendar items="vm.items"></mx-calendar>
 * ```
 *
 *
 * An `on-item-click` attribute allows to set handler on event-item click.
 * Handler function gets corresponding event-item-object as 'item' parameter
 * and link to mouse click-event as 'event' parameter.
 * ```html
 * <mx-calendar
 *        items="vm.items"
 *        on-item-click="vm.itemClickHandler(item)">
 *    </mx-calendar>
 * ```
 * (`item` attribute contains link to original event-item in `item.__originalItem` key)
 *
 * Attribute `enable-adding` allows to set adding mode,
 * which allows adding event-items directly from calendar by selecting some period on it.
 * This new event-item will be added into `items` automatically.
 *
 *
 * Attribute `enable-changing` allows to set changing mode,
 * which allows changing event-item's start and end dates by dragging and resizing them on calendar.
 * This changes of event-item will be effected on `items` automatically.
 *
 *
 * Attribute `enable-removing` allows to set removing mode,
 * which allows event-items removing directly from calendar by clicking "x"-button on event.
 * Removed event-item will be removed from `items` as well automatically.
 *
 *
 * Attribute `show-holidays` allows to show public holidays.
 * HolidaysService is used to get list of public holidays for period shown in calendar.
 *
 *
 * An `on-range-select` attribute enable selection mode and
 * allows to set handler for selection some date range on calendar.
 * Handler function gets start-date, end-date as parameters.
 * (attribute `enable-adding="true"`` should be set)
 * ```html
 *    <mx-calendar
 *        items="vm.items"
 *        enable-adding="true"
 *        on-range-select="vm.dateRangeSelectionHanlder(start, end)">
 *    </mx-calendar>
 * ```
 * An `on-item-move` attribute enables dragging and resizong events mode and
 * allows to set handler when item/event is moved or resized.
 * Handler function gets start-date, end-date and moved item as parameters.
 * (attribute `enable-changing="true"`` should be set)
 * ```html
 *    <mx-calendar
 *        items="vm.items"
 *        enable-changing="true"
 *        on-item-move="vm.eventMoveHandler(start, end, movedItem)">
 *    </mx-calendar>
 * ```
 * (`movedItem` attribute contains link to original event-item in `movedItem.__originalItem` key)
 *
 *
 * Attribute `on-item-removed` allows to set handler for the case when any event-item is removed.
 * (attribute `enable-removing="true"`` should be set)
 * ```html
 *    <mx-calendar
 *        items="vm.items"
 *        enable-removing="true"
 *        on-item-removed="vm.eventRemoveHandler(removedItem)">
 *    </mx-calendar>
 * ```
 * (`removedItem` attribute contains link to original event-item in `movedItem.__originalItem` key)
 *
 *
 * `language` attribute allows to set localization (default: `en`).
 *
 *
 * `mxCalendar` words via "load on demand" approach.
 * To configure components Dir make like this:
 * For config phase:
 * ```js
 * .config(['mx.components.LazyLoadCfgProvider', function (lazyLoadCfgProvider) {
 * 		lazyLoadCfgProvider.setComponentsDir('my_components_root/');
 * 	}])
 * ```
 * For executing phase:
 * ```js
 * .run('myCtrl', ['mx.components.LazyLoadCfg', function (lazyLoadCfg) {
 * 		lazyLoadCfg.componentsDir = 'my_components_root/';
 * 	}]);
 * ```
 *
 *
 * It's possible to hightlight Business Hours by passing attribute:
 * ```html
 *    <mx-calendar ... business-hours="vm.myBusinessHours"></mx-calendar>
 * ```
 * Format of `vm.myBusinessHours` is the same as described here https://fullcalendar.io/docs/display/businessHours/
 *
 *
 * Attributes `item-title-key`, 'item-start-key', 'item-end-key' allows to set other keys in incoming data.
 * vm.myItems = [
 *        {displayName: 'Today event',    from: '2016-10-25', to: '2016-10-26'},
 *        {displayName: 'First 2000 day', from: '2016-10-27', to: '2016-10-28'},
 *        ...
 * ]
 * ```html
 * <mx-calendar
 *        items="vm.myItems"
 *        item-title-key="displayName"
 *        item-start-key="from"
 *        item-end-key>
 *    </mx-calendar>
 * ```
 *
 *
 * Attribute `abstract-mode` allows to set an Abstract mode,
 * which is mean that calendar isn't connected with some particular dates or timezone (uses "UTC")
 * and doesn't show header with some particular dates.
 *
 *
 * Attribute `on-range-changed` allows to set handler for the case when
 * showing time period on calendar will be changed.
 * For example: Switching on other month or week.
 *
 *
 * Attribute `event-overlap` (default: `true`) allows/forbids event-items overlapping
 * when uses trying to add new or change existent event.
 *
 * Attribute `disabled` (default: `false`) allows to set Calendar in disabled mode.
 *
 **/
angular.module('mx.SolutionBuilderAgent').component('mxCalendar', {
	bindings: {
		items: '=',
		itemsLoader: '=?',
		onItemClick: '&',
		onItemRightClick: '&',
		dropdownMenuOptions: '=',
		onRangeSelect: '=?',
		onItemMove: '=?',
		onItemRemoved: '&',
		language: '@',
		businessHours: '=?',
		height: '@',
		autoWidth: '<', // if true then calendar can use more width than parentWidth with styled width of columns

		itemTitleKey: '@',
		itemDescriptionKey: '@',
		itemStartKey: '@',
		itemEndKey: '@',
		itemColorKey: '@',
		itemWholeDayKey: '@',
		itemReadonlyKey: '@',
		itemExtraDimensionKey: '@',
		itemBackgroundKey: '@',

		calendarView: '@defaultTimeView',
		contextId: '@',
		abstractMode: '@',
		enableAdding: '@',
		enableChanging: '@',
		enableRemoving: '@',
		eventOverlap: '@',
		disabled: '@',
		showHolidays: '@',

		showUnassigned: '<',
		showDayView: '<',
		showWeekView: '<',
		showMonthView: '<',
		extraDimensions: '<',
		isTimeline: '<',
	},
	controller: CalendarController,
	controllerAs: 'vm',
	template: template,
});
